'use client'

import { uploadFileRestAPI } from '@/services/upload'
import { useState, useCallback } from 'react'
import { toast } from 'react-toastify'

export interface IUploadedMedia {
  id: string
  file: File
  preview: string
  type: 'image' | 'video'
  uploading?: boolean
  error?: string
}

export const useMediaUpload = () => {
  const [uploadedMedia, setUploadedMedia] = useState<IUploadedMedia[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Validate file
  const validateFile = useCallback((file: File, isImage: boolean) => {
    // Kiểm tra kích thước file (max 10MB cho ảnh, 50MB cho video)
    const maxSize = isImage ? 10 * 1024 * 1024 : 50 * 1024 * 1024
    const maxSizeText = isImage ? '10MB' : '50MB'

    if (file.size > maxSize) {
      throw new Error(`File ${file.name} quá lớn. Kích thước tối đa: ${maxSizeText}`)
    }
  }, [])

  // Thêm media vào danh sách
  const addMedia = useCallback(
    (
      files: FileList,
      maxImages: number,
      maxVideos: number,
      acceptedImageTypes: string[],
      acceptedVideoTypes: string[]
    ) => {
      const filesArray = Array.from(files)
      const currentImages = uploadedMedia.filter((m) => m.type === 'image').length
      const currentVideos = uploadedMedia.filter((m) => m.type === 'video').length

      const newMedia: IUploadedMedia[] = []

      filesArray.forEach((file) => {
        const isImage = acceptedImageTypes.includes(file.type)
        const isVideo = acceptedVideoTypes.includes(file.type)

        if (!isImage && !isVideo) {
          toast.error(`File ${file.name} không được hỗ trợ`)
          return
        }

        if (isImage && currentImages + newMedia.filter((m) => m.type === 'image').length >= maxImages) {
          toast.error(`Chỉ được tải lên tối đa ${maxImages} ảnh`)
          return
        }

        if (isVideo && currentVideos + newMedia.filter((m) => m.type === 'video').length >= maxVideos) {
          toast.error(`Chỉ được tải lên tối đa ${maxVideos} video`)
          return
        }

        // Validate file size
        try {
          validateFile(file, isImage)
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Lỗi validate file')
          return
        }

        const media: IUploadedMedia = {
          id: `${Date.now()}-${Math.random()}`,
          file,
          preview: URL.createObjectURL(file),
          type: isImage ? 'image' : 'video',
          uploading: false,
        }

        newMedia.push(media)
      })

      if (newMedia.length > 0) {
        setUploadedMedia((prev) => [...prev, ...newMedia])
      }
    },
    [uploadedMedia, validateFile]
  )

  // Xóa media khỏi danh sách
  const removeMedia = useCallback((id: string) => {
    setUploadedMedia((prev) => {
      const media = prev.find((m) => m.id === id)
      if (media?.preview) {
        URL.revokeObjectURL(media.preview)
      }
      return prev.filter((m) => m.id !== id)
    })
  }, [])

  // Upload media lên server
  const uploadMedia = useCallback(async (): Promise<string[]> => {
    if (uploadedMedia.length === 0) {
      return []
    }

    const uploadedUrls: string[] = []

    // Upload từng file và cập nhật trạng thái
    for (let i = 0; i < uploadedMedia.length; i++) {
      const media = uploadedMedia[i]
      if (!media) continue

      // Set uploading state cho file hiện tại
      setUploadedMedia((prev) => prev.map((m, index) => (index === i ? { ...m, uploading: true } : m)))

      try {
        // Upload file lên server
        const result = await uploadFileRestAPI({
          file: media.file,
          module: 'image', // Module name theo API
        })

        if (result.success && result.data?.url) {
          uploadedUrls.push(result.data.url)

          // Set success state
          setUploadedMedia((prev) => prev.map((m, index) => (index === i ? { ...m, uploading: false } : m)))
        } else {
          throw new Error(result.message || 'Upload thất bại')
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Lỗi không xác định'
        console.error(`Upload ${media.file.name} failed:`, errorMessage)

        // Set error state
        setUploadedMedia((prev) =>
          prev.map((m, index) => (index === i ? { ...m, uploading: false, error: errorMessage } : m))
        )

        toast.error(`Upload "${media.file.name}" thất bại: ${errorMessage}`)
        throw error
      }
    }

    return uploadedUrls
  }, [uploadedMedia])

  // Reset về trạng thái ban đầu
  const reset = useCallback(() => {
    uploadedMedia.forEach((media) => {
      if (media.preview) {
        URL.revokeObjectURL(media.preview)
      }
    })
    setUploadedMedia([])
  }, [uploadedMedia])

  return {
    uploadedMedia,
    isSubmitting,
    setIsSubmitting,
    addMedia,
    removeMedia,
    uploadMedia,
    reset,
  }
}
